
import React, { useState } from 'react';
import { ICONS, BRAND } from '../constants';
import { AppState } from '../types';

interface SidebarProps {
  currentView: AppState['view'];
  onNavigate: (view: AppState['view']) => void;
  onClose?: () => void;
  onConnectCascade?: () => void;
  isCollapsed: boolean;
  onCollapseToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  currentView, 
  onNavigate, 
  onClose, 
  onConnectCascade, 
  isCollapsed, 
  onCollapseToggle 
}) => {
  const [isFutureExpanded, setIsFutureExpanded] = useState(false);

  const mainMenuItems: { id: AppState['view']; label: string; icon: React.FC }[] = [
    { id: 'overview', label: 'OVERVIEW', icon: ICONS.Dashboard },
    { id: 'rota', label: 'ROTAS', icon: ICONS.Rota },
    { id: 'locations', label: 'SITES & HOURS', icon: ICONS.Locations },
    { id: 'people', label: 'PEOPLE', icon: ICONS.People },
  ];

  const futureMenuItems: { id: AppState['view']; label: string; icon: React.FC }[] = [
    { id: 'jobs', label: 'JOBS', icon: () => (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    )},
    { id: 'forms', label: 'FORMS', icon: () => (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )},
    { id: 'leave', label: 'LEAVE', icon: ICONS.Leave },
    { id: 'timesheets', label: 'TIMESHEETS', icon: ICONS.Timesheets },
    { id: 'settings', label: 'SETTINGS', icon: ICONS.Settings },
  ];

  return (
    <aside className={`bg-black text-white flex flex-col h-full shadow-2xl transition-all duration-300 relative ${isCollapsed ? 'w-20' : 'w-full'}`}>
      <button 
        onClick={onCollapseToggle}
        className="hidden lg:flex absolute -right-3 top-12 w-6 h-6 bg-ymca-orange text-white items-center justify-center border-2 border-black rounded-none z-50 hover:scale-110 transition-transform"
      >
        <svg className={`w-3 h-3 transition-transform ${isCollapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={3} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <div className={`p-6 border-b border-white/10 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
        {!isCollapsed ? (
          <img src={BRAND.logoUrl} alt="YMCA Logo" className="h-8 object-contain" />
        ) : (
          <div className="w-8 h-8 bg-white text-black flex items-center justify-center font-black text-xl ymca-brand-font">Y</div>
        )}
        {onClose && !isCollapsed && (
          <button onClick={onClose} className="lg:hidden p-2 text-gray-400 hover:text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <nav className="flex-1 p-3 lg:p-4 space-y-2 overflow-y-auto scrollbar-hide">
        {!isCollapsed && <div className="text-[10px] font-bold text-gray-500 tracking-[0.2em] mb-4 pl-4 uppercase">Operational Hub</div>}
        
        {mainMenuItems.map((item) => (
          <button
            key={item.id}
            onClick={() => onNavigate(item.id)}
            title={isCollapsed ? item.label : ''}
            className={`w-full flex items-center space-x-4 p-3 transition-all duration-200 group ${
              currentView === item.id 
                ? 'bg-white text-black' 
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            } ${isCollapsed ? 'justify-center' : ''}`}
          >
            <div className={`transition-colors duration-200 ${currentView === item.id ? 'text-black' : 'text-gray-500 group-hover:text-white'}`}>
              <item.icon />
            </div>
            {!isCollapsed && <span className="ymca-brand-font text-xl tracking-wider uppercase leading-none mt-1">{item.label}</span>}
          </button>
        ))}

        <div className="mt-8 border-t border-white/10 pt-4">
          {!isCollapsed && (
            <button 
              onClick={() => setIsFutureExpanded(!isFutureExpanded)}
              className="w-full flex items-center justify-between px-4 py-2 text-ymca-orange"
            >
              <span className="ymca-brand-font text-2xl tracking-widest leading-none mt-1 uppercase">Advanced</span>
              <svg className={`w-4 h-4 transition-transform duration-300 ${isFutureExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}
          
          <div className={`${!isCollapsed && !isFutureExpanded ? 'hidden' : 'block'} space-y-1 mt-2`}>
            {futureMenuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                title={isCollapsed ? item.label : ''}
                className={`w-full flex items-center space-x-4 p-3 transition-all duration-200 group ${
                  currentView === item.id 
                    ? 'bg-white/10 text-white' 
                    : 'text-gray-600 hover:text-white hover:bg-white/5'
                } ${isCollapsed ? 'justify-center' : ''}`}
              >
                <div className="scale-90"><item.icon /></div>
                {!isCollapsed && <span className="ymca-brand-font text-lg tracking-wider uppercase leading-none mt-0.5">{item.label}</span>}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <div className={`p-4 border-t border-white/10 space-y-4 ${isCollapsed ? 'items-center' : ''}`}>
        {!isCollapsed ? (
          <button 
            onClick={onConnectCascade}
            className="w-full bg-ymca-orange/10 text-ymca-orange py-3.5 px-4 rounded-none transition-all hover:bg-ymca-orange hover:text-white group flex items-center justify-center space-x-2 border-2 border-ymca-orange"
          >
            <span className="ymca-brand-font text-xl tracking-wider leading-none mt-1 uppercase truncate">Connect Cascade</span>
          </button>
        ) : (
          <button onClick={onConnectCascade} title="Connect Cascade" className="w-12 h-12 mx-auto flex items-center justify-center border-2 border-ymca-orange text-ymca-orange hover:bg-ymca-orange hover:text-white transition-all">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm4.59-12.42L10 14.17l-2.59-2.58L6 13l4 4 8-8z"/></svg>
          </button>
        )}

        <div className={`flex items-center space-x-4 p-3 bg-white/5 border border-white/10 rounded-none ${isCollapsed ? 'justify-center px-0' : ''}`}>
          <div className="w-10 h-10 flex-shrink-0 bg-white flex items-center justify-center font-bold text-black text-lg">HI</div>
          {!isCollapsed && (
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-bold truncate uppercase tracking-tighter leading-none mb-1">Hayley Isacc</p>
              <p className="text-[9px] text-gray-500 font-bold uppercase tracking-widest">Global Admin</p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
